import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

import type { ReactTable, RowData } from '@tanstack/react-table';

import { Button } from '@/shared/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';

import styles from './DataTable.module.scss';
import type { DataTableFeatures } from './tableFeatures';

const PAGE_SIZES = [10, 25, 50, 100];

export interface DataTablePaginationProps<TData extends RowData> {
  table: ReactTable<DataTableFeatures, TData>;
  pageSizes?: number[];
}

/**
 * Страницы и размер страницы.
 *
 * Показывает не только номер страницы, но и диапазон строк: «26–50 из 137».
 * Один номер страницы ничего не говорит, пока не знаешь, по сколько строк
 * они нарезаны, — а размер страницы человек меняет сам.
 */
export function DataTablePagination<TData extends RowData>({
  table,
  pageSizes = PAGE_SIZES,
}: DataTablePaginationProps<TData>) {
  const { pageIndex, pageSize } = table.state.pagination;

  const total = table.getRowCount();
  const pageCount = table.getPageCount();

  const first = total === 0 ? 0 : pageIndex * pageSize + 1;
  const last = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <div className={styles['pagination']}>
      <div className={styles['pagination__size']}>
        <span className={styles['pagination__label']}>Строк на странице</span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              tone="neutral"
              size="sm"
              iconRight={<ChevronDown size={14} />}
              aria-label={`Строк на странице: ${String(pageSize)}`}
            >
              {pageSize}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuRadioGroup
              value={String(pageSize)}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              {pageSizes.map((size) => (
                <DropdownMenuRadioItem key={size} value={String(size)}>
                  {size}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Диапазон меняется при переходе по страницам, и объявить его надо,
          не перебивая работу: aria-live="polite" дождётся паузы. */}
      <p className={styles['pagination__range']} aria-live="polite">
        {total === 0 ? 'Ничего не найдено' : `${String(first)}–${String(last)} из ${String(total)}`}
      </p>

      <div className={styles['pagination__pages']}>
        <Button
          iconOnly
          variant="ghost"
          tone="neutral"
          size="sm"
          aria-label="В начало"
          disabled={!table.getCanPreviousPage()}
          onClick={() => {
            table.firstPage();
          }}
        >
          <ChevronsLeft size={16} />
        </Button>

        <Button
          iconOnly
          variant="ghost"
          tone="neutral"
          size="sm"
          aria-label="Предыдущая страница"
          disabled={!table.getCanPreviousPage()}
          onClick={() => {
            table.previousPage();
          }}
        >
          <ChevronLeft size={16} />
        </Button>

        <span className={styles['pagination__page']}>
          {Math.min(pageIndex + 1, Math.max(pageCount, 1))} / {Math.max(pageCount, 1)}
        </span>

        <Button
          iconOnly
          variant="ghost"
          tone="neutral"
          size="sm"
          aria-label="Следующая страница"
          disabled={!table.getCanNextPage()}
          onClick={() => {
            table.nextPage();
          }}
        >
          <ChevronRight size={16} />
        </Button>

        <Button
          iconOnly
          variant="ghost"
          tone="neutral"
          size="sm"
          aria-label="В конец"
          disabled={!table.getCanNextPage()}
          onClick={() => {
            table.lastPage();
          }}
        >
          <ChevronsRight size={16} />
        </Button>
      </div>
    </div>
  );
}
